import React, { useEffect, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { billingApi, getErrorMessage } from '@/lib/api';
import { formatNumber } from '@/lib/utils';
import Button from '@/components/ui/Button';
import { Panel } from '@/components/ui/Panel';
import { fieldControl, fieldHint, fieldLabel } from '@/components/ui/fieldStyles';

// The balance below which the gateway emails a warning (once per drop).
const LowBalanceSetting: React.FC<{ threshold?: number }> = ({ threshold }) => {
  const queryClient = useQueryClient();
  const [value, setValue] = useState('');
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<{ ok: boolean; text: string } | null>(null);

  useEffect(() => {
    if (threshold !== undefined) setValue(String(threshold));
  }, [threshold]);

  const parsed = Number(value);
  const valid = value.trim() !== '' && Number.isInteger(parsed) && parsed >= 0;

  const save = async (next: number) => {
    setBusy(true);
    setMessage(null);
    try {
      await billingApi.setLowBalanceThreshold(next);
      setValue(String(next));
      await queryClient.invalidateQueries();
      setMessage({ ok: true, text: next === 0 ? 'Low-balance warning turned off.' : `We'll email you when your balance drops below ${formatNumber(next)} credits.` });
    } catch (err) {
      setMessage({ ok: false, text: getErrorMessage(err) });
    } finally {
      setBusy(false);
    }
  };

  return (
    <Panel title="Low-balance warning">
      <p className="text-sm text-muted-foreground">
        We email you once when your balance drops below this level, so your API calls don't stop without notice.
      </p>
      <div className="mt-4 flex flex-col gap-3 sm:flex-row sm:items-end">
        <div className="flex-1">
          <label htmlFor="low-balance" className={fieldLabel}>Warn me below (credits)</label>
          <input
            id="low-balance"
            type="number"
            min={0}
            step={1000}
            inputMode="numeric"
            value={value}
            onChange={(e) => setValue(e.target.value)}
            className={`${fieldControl} font-mono tabular-nums`}
          />
          {valid && parsed > 0 && (
            <p className={fieldHint}>Worth ₦{formatNumber(parsed / 10)}.</p>
          )}
        </div>
        <div className="flex gap-2">
          <Button className="h-11" onClick={() => save(parsed)} disabled={!valid || busy} loading={busy}>Save</Button>
          {threshold !== 0 && (
            <Button variant="outline" className="h-11" onClick={() => save(0)} disabled={busy}>Turn off</Button>
          )}
        </div>
      </div>
      {message && <p className={`mt-3 text-sm ${message.ok ? 'text-success' : 'text-error'}`} role="status">{message.text}</p>}
    </Panel>
  );
};

export default LowBalanceSetting;
